export const POSTING_SOURCE_IDS = {
  unified: 0,
  revelioWebsitePostings: 8,
  otherWebsitePostings: 2,
  linkedIn: 3,
  indeed: 4,
} as const;
export type PostingSourceId =
  (typeof POSTING_SOURCE_IDS)[keyof typeof POSTING_SOURCE_IDS];

/**
 * How each posting source is NAMED — the single definition of the words, beside
 * the ids. The page's source filter and the assistant's postings SCOPE line
 * both derive from it, so a rename lands on both at once; held apart, the two
 * silently disagreed about what a feed was called.
 */
export const POSTING_SOURCE_LABELS = {
  [POSTING_SOURCE_IDS.unified]: 'Unified (COSMOS)',
  [POSTING_SOURCE_IDS.otherWebsitePostings]: 'Website Postings (other)',
  [POSTING_SOURCE_IDS.revelioWebsitePostings]: 'Revelio Labs Website Postings',
  [POSTING_SOURCE_IDS.linkedIn]: 'LinkedIn',
  [POSTING_SOURCE_IDS.indeed]: 'Indeed',
  // `as const satisfies`, not a `Record<PostingSourceId, string>` annotation:
  // the annotation widens every value to `string`, and the extraction schema
  // needs these as LITERALS to build a `z.enum` from them. `satisfies` still
  // fails the build if an id is missing or misspelt.
} as const satisfies Record<PostingSourceId, string>;

/**
 * The source names the extraction model must answer with, as a TUPLE so the
 * schema can be a `z.enum` rather than free text re-matched by word. Display
 * order, which is also the page's.
 *
 * Listed rather than derived from the table's keys: `Object.keys` on a
 * numeric-keyed record returns them in ascending NUMERIC order, which is not
 * this order, and `z.enum` needs a non-empty literal tuple either way.
 */
export const POSTING_SOURCE_NAMES = [
  POSTING_SOURCE_LABELS[POSTING_SOURCE_IDS.unified],
  POSTING_SOURCE_LABELS[POSTING_SOURCE_IDS.otherWebsitePostings],
  POSTING_SOURCE_LABELS[POSTING_SOURCE_IDS.revelioWebsitePostings],
  POSTING_SOURCE_LABELS[POSTING_SOURCE_IDS.linkedIn],
  POSTING_SOURCE_LABELS[POSTING_SOURCE_IDS.indeed],
] as const;

/**
 * A missing source would make `POSTING_SOURCE_NAMES` a narrower union than the
 * label table, and the model would be offered fewer boards than the page. The
 * assignment IS the check; a bare type alias would be inert.
 */
type _EveryPostingSourceIsNamed =
  (typeof POSTING_SOURCE_LABELS)[PostingSourceId] extends (typeof POSTING_SOURCE_NAMES)[number]
    ? true
    : ['posting source missing from POSTING_SOURCE_NAMES'];
const _EVERY_POSTING_SOURCE_IS_NAMED: _EveryPostingSourceIsNamed = true;
void _EVERY_POSTING_SOURCE_IS_NAMED;

/**
 * The employee-review star ratings the rating filter offers, low to high. The
 * value and the label are the same digit, so one tuple serves both the
 * dashboard's option list and the extraction schema's enum.
 */
export const OVERALL_RATING_VALUES = ['1', '2', '3', '4', '5'] as const;

/**
 * How long dash-meta waits on the Economic Impact MRIO API: its ping, a
 * solve (~15 s placed by county, up to 33 s by city, where the location bridge
 * maps every tree), and a role split of a cached solve (no model run; its
 * timing is unmeasured, so it gets the ping's budget for now). The proxy and
 * the health-check probes share them, so a probe gives the API the same time
 * the page does.
 */
export const ECONOMIC_IMPACT_TIMEOUT_MS = {
  ping: 30_000,
  solve: 60_000,
  roles: 30_000,
} as const;
